import { v4 as uuidv4 } from "uuid";
/**
 * @author AgentStore
 * @description 格式化时间 (支持i18n)
 * @param time
 * @param cFormat
 * @param locale - 可选的语言环境，如果不传则使用当前i18n语言
 * @returns {string|null}
 */
export function parseTime(time, cFormat, locale) {
  if (arguments.length === 0) {
    return null
  }

  // 获取当前语言环境
  let currentLocale = locale
  if (!currentLocale) {
    try {
      // 动态导入i18n以避免循环依赖
      const { getLocale } = require('@/locales/i18n')
      currentLocale = getLocale() || 'zh-cn'
    } catch (e) {
      currentLocale = 'zh-cn' // 默认中文
    }
  }

  const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}'
  let date
  if (typeof time === 'object') {
    date = time
  } else {
    if (typeof time === 'string' && /^[0-9]+$/.test(time)) {
      time = parseInt(time)
    }
    if (typeof time === 'number' && time.toString().length === 10) {
      time = time * 1000
    }
    date = new Date(time)
  }

  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay()
  }

  // 根据语言环境获取星期数据
  const getWeekDays = (locale) => {
    const weekDaysMap = {
      'zh-cn': ['日', '一', '二', '三', '四', '五', '六'],
      'zh-tw': ['日', '一', '二', '三', '四', '五', '六'],
      'en-us': ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    }
    return weekDaysMap[locale] || weekDaysMap['zh-cn']
  }

  // 年月日单位i18n替换
  const unitMap = {
    'zh-cn': { year: '年', month: '月', day: '日' },
    'zh-tw': { year: '年', month: '月', day: '日' },
    'en-us': { year: '/', month: '/', day: '' }
  }
  const units = unitMap[currentLocale] || unitMap['zh-cn']
  // 先替换模板中的单位
  let localizedFormat = format
    .replace(/年/g, units.year)
    .replace(/月/g, units.month)
    .replace(/日/g, units.day)
  return localizedFormat.replace(/{([ymdhisa])+}/g, (result, key) => {
    let value = formatObj[key]
    if (key === 'a') {
      return getWeekDays(currentLocale)[value]
    }
    const shouldAddLeadingZero = (key, locale) => {
      if ((key === 'm' || key === 'd') && (locale === 'zh-cn' || locale === 'zh-tw')) {
        return false
      }
      return true
    }
    if (result.length > 0 && value < 10 && shouldAddLeadingZero(key, currentLocale)) {
      value = '0' + value
    }
    return value || 0
  })
}

/**
 * @author AgentStore
 * @description 格式化时间 (支持i18n)
 * @param time
 * @param option
 * @param locale - 可选的语言环境，如果不传则使用当前i18n语言
 * @returns {string}
 */
export function formatTime(time, option, locale) {
  if (('' + time).length === 10) {
    time = parseInt(time) * 1000
  } else {
    time = '' + time
  }

  // 获取当前语言环境
  let currentLocale = locale
  if (!currentLocale) {
    try {
      const { getLocale } = require('@/locales/i18n')
      currentLocale = getLocale() || 'zh-cn'
    } catch (e) {
      currentLocale = 'zh-cn'
    }
  }

  const d = new Date(time)
  const now = Date.now()
  const diff = (now - d) / 1000

  // 根据语言环境获取相对时间文本
  const getRelativeTimeText = (locale) => {
    const textMap = {
      'zh-cn': {
        just_now: '刚刚',
        minutes_ago: '分钟前',
        hours_ago: '小时前',
        yesterday: '昨天'
      },
      'zh-tw': {
        just_now: '剛剛',
        minutes_ago: '分鐘前',
        hours_ago: '小時前',
        yesterday: '昨天'
      },
      'en-us': {
        just_now: 'Just now',
        minutes_ago: 'm ago',
        hours_ago: 'h ago',
        yesterday: 'Yesterday'
      }
    }
    return textMap[locale] || textMap['zh-cn']
  }

  // 获取默认日期格式
  const getDefaultFormat = (locale) => {
    const formatMap = {
      'zh-cn': '{m}月{d}日',
      'zh-tw': '{m}月{d}日',
      'en-us': '{m}/{d}'
    }
    return formatMap[locale] || formatMap['zh-cn']
  }

  // 智能格式化 - 根据传入的format和当前语言进行智能转换
  const getSmartFormat = (format, locale) => {
    if (!format) return getDefaultFormat(locale)
    // 常见中英文年月日格式自动转换
    const formatConversionMap = {
      'zh-cn': {
        '{y}-{m}-{d}': '{y}年{m}月{d}日',
        '{y}/{m}/{d}': '{y}年{m}月{d}日',
        '{m}/{d}': '{m}月{d}日',
        '{m}-{d}': '{m}月{d}日',
        '{d}/{m}': '{d}日{m}月',
        '{d}-{m}': '{d}日{m}月',
        '{y}.{m}.{d}': '{y}年{m}月{d}日',
        '{y}年{m}月{d}日': '{y}年{m}月{d}日',
      },
      'zh-tw': {
        '{y}-{m}-{d}': '{y}年{m}月{d}日',
        '{y}/{m}/{d}': '{y}年{m}月{d}日',
        '{m}/{d}': '{m}月{d}日',
        '{m}-{d}': '{m}月{d}日',
        '{d}/{m}': '{d}日{m}月',
        '{d}-{m}': '{d}日{m}月',
        '{y}.{m}.{d}': '{y}年{m}月{d}日',
        '{y}年{m}月{d}日': '{y}年{m}月{d}日',
      },
      'en-us': {
        '{y}年{m}月{d}日': '{m}/{d}/{y}',
        '{y}年{m}月{d}': '{m}/{d}/{y}',
        '{y}-{m}-{d}': '{m}/{d}/{y}',
        '{y}/{m}/{d}': '{m}/{d}/{y}',
        '{y}.{m}.{d}': '{m}/{d}/{y}',
        '{m}月{d}日': '{m}/{d}',
        '{m}月{d}': '{m}/{d}',
        '{d}日{m}月': '{d}/{m}',
        '{d}日{m}': '{d}/{m}'
      }
    }
    const conversionRules = formatConversionMap[locale] || {}
    return conversionRules[format] || format
  }

  const timeText = getRelativeTimeText(currentLocale)

  if (diff < 30) {
    return timeText.just_now
  } else if (diff < 3600) {
    const minutes = Math.ceil(diff / 60)
    return currentLocale === 'en-us' ?
      `${minutes}${timeText.minutes_ago}` :
      `${minutes}${timeText.minutes_ago}`
  } else if (diff < 3600 * 24) {
    const hours = Math.ceil(diff / 3600)
    return currentLocale === 'en-us' ?
      `${hours}${timeText.hours_ago}` :
      `${hours}${timeText.hours_ago}`
  } else if (diff < 3600 * 24 * 2) {
    return timeText.yesterday
  }

  if (option) {
    // 使用智能格式化转换
    const smartFormat = getSmartFormat(option, currentLocale)
    return parseTime(time, smartFormat, currentLocale)
  } else {
    return parseTime(time, getDefaultFormat(currentLocale), currentLocale)
  }
}
export function deepClone(target, result) {
  // 定义一个变量
  result = result || {}

  // 如果当前需要深拷贝的是一个对象的话
  if (typeof target === 'object') {
    // 如果是一个数组的话
    if (Array.isArray(target)) {
      if (Array.isArray(result)) {
        result.splice(0)
      } else {
        result = [] // 将result赋值为一个数组，并且执行遍历
      }
      for (const i in target) {
        // 递归克隆数组中的每一项
        result.push(deepClone(target[i]))
      }
      // 判断如果当前的值是null的话；直接赋值为null
    } else if (target === null) {
      result = null
      // 判断如果当前的值是一个RegExp对象的话，直接赋值
    } else if (target.constructor === RegExp) {
      result = target
    } else {
      // 否则是普通对象，直接for in循环，递归赋值对象的所有值
      // result = {};
      for (const i in target) {
        result[i] = deepClone(target[i])
      }
    }
    // 如果不是对象的话，就是基本数据类型，那么直接赋值
  } else {
    result = target
  }
  // 返回最终结果
  return result
}
/**
 * @author AgentStore
 * @description 将url请求参数转为json格式
 * @param url
 * @returns {{}|any}
 */
export function paramObj(url) {
  const search = url.split('?')[1]
  if (!search) {
    return {}
  }
  return JSON.parse(
    '{"' +
      decodeURIComponent(search)
        .replace(/"/g, '\\"')
        .replace(/&/g, '","')
        .replace(/=/g, '":"')
        .replace(/\+/g, ' ') +
      '"}'
  )
}

/**
 * @author AgentStore
 * @description 父子关系的数组转换成树形结构数据
 * @param data
 * @returns {*}
 */
export function translateDataToTree(data) {
  const parent = data.filter(
    (value) => value.parentId === 'undefined' || value.parentId == null|| value.parentId<1
  )
  const children = data.filter(
    (value) => value.parentId !== 'undefined' && value.parentId != null && value.parentId >0
  )
  const translator = (parent, children) => {
    parent.forEach((parent) => {
      children.forEach((current, index) => {
        if (current.parentId === parent.id) {
          const temp = JSON.parse(JSON.stringify(children))
          temp.splice(index, 1)
          translator([current], temp)
          typeof parent.children !== 'undefined' ? parent.children.push(current) : (parent.children = [current])
        }
      })
    })
  }
  translator(parent, children)
  return parent
}

/**
* 提取树的id，保持树结构
* */
export function extractTreeIds(treeData) {
  return treeData.map(node => {
    const newNode = {
      id: node.id
    };
    // 如果有子节点，递归处理
    if (node.children && node.children.length > 0) {
      newNode.children = extractTreeIds(node.children);
    } else {
      newNode.children = [];
    }

    return newNode;
  });
}

/**
 * @author AgentStore
 * @description 树形结构数据转换成父子关系的数组
 * @param data
 * @returns {[]}
 */
export function translateTreeToData(data) {
  const result = []
  data.forEach((item) => {
    const loop = (data) => {
      result.push({
        id: data.id,
        name: data.name,
        parentId: data.parentId
      })
      const child = data.children
      if (child) {
        for (let i = 0; i < child.length; i++) {
          loop(child[i])
        }
      }
    }
    loop(item)
  })
  return result
}

/**
 * @author AgentStore
 * @description 10位时间戳转换 (支持i18n)
 * @param time
 * @param locale - 可选的语言环境，如果不传则使用当前i18n语言
 * @returns {string}
 */
export function tenBitTimestamp(time, locale) {
  // 获取当前语言环境
  let currentLocale = locale
  if (!currentLocale) {
    try {
      const { getLocale } = require('@/locales/i18n')
      currentLocale = getLocale() || 'zh-cn'
    } catch (e) {
      currentLocale = 'zh-cn'
    }
  }

  const date = new Date(time * 1000)
  const y = date.getFullYear()
  let m = date.getMonth() + 1
  let d = date.getDate()
  let h = date.getHours()
  let minute = date.getMinutes()
  let second = date.getSeconds()

  const formatNumber = (num, addZero = true) => {
    return addZero && num < 10 ? '0' + num : num.toString()
  }

  const isChinese = currentLocale === 'zh-cn' || currentLocale === 'zh-tw'

  m = formatNumber(m, !isChinese)
  d = formatNumber(d, !isChinese)
  h = formatNumber(h, true)
  minute = formatNumber(minute, true)
  second = formatNumber(second, true)

  // 根据语言环境返回不同格式
  const formatMap = {
    'zh-cn': `${y}年${m}月${d}日 ${h}:${minute}:${second}`,
    'zh-tw': `${y}年${m}月${d}日 ${h}:${minute}:${second}`,
    'en-us': `${m}/${d}/${y} ${h}:${minute}:${second}`
  }

  return formatMap[currentLocale] || formatMap['zh-cn']
}

/**
 * @author AgentStore
 * @description 13位时间戳转换 (支持i18n)
 * @param time
 * @param locale - 可选的语言环境，如果不传则使用当前i18n语言
 * @returns {string}
 */
export function thirteenBitTimestamp(time, locale) {
  // 获取当前语言环境
  let currentLocale = locale
  if (!currentLocale) {
    try {
      const { getLocale } = require('@/locales/i18n')
      currentLocale = getLocale() || 'zh-cn'
    } catch (e) {
      currentLocale = 'zh-cn'
    }
  }

  const date = new Date(time / 1)
  const y = date.getFullYear()
  let m = date.getMonth() + 1
  let d = date.getDate()
  let h = date.getHours()
  let minute = date.getMinutes()
  let second = date.getSeconds()

  const formatNumber = (num, addZero = true) => {
    return addZero && num < 10 ? '0' + num : num.toString()
  }

  const isChinese = currentLocale === 'zh-cn' || currentLocale === 'zh-tw'

  m = formatNumber(m, !isChinese)
  d = formatNumber(d, !isChinese)
  h = formatNumber(h, true)
  minute = formatNumber(minute, true)
  second = formatNumber(second, true)

  // 根据语言环境返回不同格式
  const formatMap = {
    'zh-cn': `${y}年${m}月${d}日 ${h}:${minute}:${second}`,
    'zh-tw': `${y}年${m}月${d}日 ${h}:${minute}:${second}`,
    'en-us': `${m}/${d}/${y} ${h}:${minute}:${second}`
  }

  return formatMap[currentLocale] || formatMap['zh-cn']
}


/**
 * uuid 生成函数
 * @param split 分隔符
 */
export const genUid = (split = "") => {
  return uuidv4().split("-").join(split);
};

/**
 * 计算字符串的hash值
 * 返回一个数字
 * @param str
 */
export const hashCode = (str) => {
  let hash = 0;
  if (str.length === 0)
    return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
};

/**
 * @author AgentStore
 * @description m到n的随机数
 * @param m
 * @param n
 * @returns {number}
 */
export function random(m, n) {
  return Math.floor(Math.random() * (m - n) + n)
}

/**
 * @author AgentStore
 * @description addEventListener
 * @type {function(...[*]=)}
 */
export const on = (function() {
  return function(element, event, handler, useCapture = false) {
    if (element && event && handler) {
      element.addEventListener(event, handler, useCapture)
    }
  }
})()
/**
 * 是否移动设备类型
 * */
export const isMobileType = {
  Android: function() {
    return navigator.userAgent.match(/Android/i)
  },
  BlackBerry: function() {
    return navigator.userAgent.match(/BlackBerry|BB10/i)
  },
  iOS: function() {
    return navigator.userAgent.match(/iPhone|iPad|iPod/i)
  },
  Opera: function() {
    return navigator.userAgent.match(/Opera Mini/i)
  },
  Windows: function() {
    return navigator.userAgent.match(/IEMobile/i)
  },
  isSafari: function() {
    return /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
  },
  any: function() {
    return (isMobileType.Android() || isMobileType.BlackBerry() || isMobileType.iOS() || isMobileType.Opera() || isMobileType.Windows())
  },
  getOsName: function() {
    var osName = 'Unknown OS'
    if (isMobileType.Android()) {
      osName = 'Android'
    }

    if (isMobileType.BlackBerry()) {
      osName = 'BlackBerry'
    }

    if (isMobileType.iOS()) {
      osName = 'iOS'
    }

    if (isMobileType.Opera()) {
      osName = 'Opera Mini'
    }

    if (isMobileType.Windows()) {
      osName = 'Windows'
    }
    return {
      osName,
      type: 'mobile'
    }
  }
}

/**
 * 查看桌面系统
 * */
export function detectDesktopOS() {
  var unknown = '-'

  var nVer = navigator.appVersion
  var nAgt = navigator.userAgent

  var os = unknown
  var clientStrings = [{
    s: 'Chrome OS',
    r: /CrOS/
  }, {
    s: 'Windows 10',
    r: /(Windows 10.0|Windows NT 10.0)/
  }, {
    s: 'Windows 8.1',
    r: /(Windows 8.1|Windows NT 6.3)/
  }, {
    s: 'Windows 8',
    r: /(Windows 8|Windows NT 6.2)/
  }, {
    s: 'Windows 7',
    r: /(Windows 7|Windows NT 6.1)/
  }, {
    s: 'Windows Vista',
    r: /Windows NT 6.0/
  }, {
    s: 'Windows Server 2003',
    r: /Windows NT 5.2/
  }, {
    s: 'Windows XP',
    r: /(Windows NT 5.1|Windows XP)/
  }, {
    s: 'Windows 2000',
    r: /(Windows NT 5.0|Windows 2000)/
  }, {
    s: 'Windows ME',
    r: /(Win 9x 4.90|Windows ME)/
  }, {
    s: 'Windows 98',
    r: /(Windows 98|Win98)/
  }, {
    s: 'Windows 95',
    r: /(Windows 95|Win95|Windows_95)/
  }, {
    s: 'Windows NT 4.0',
    r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/
  }, {
    s: 'Windows CE',
    r: /Windows CE/
  }, {
    s: 'Windows 3.11',
    r: /Win16/
  }, {
    s: 'Android',
    r: /Android/
  }, {
    s: 'Open BSD',
    r: /OpenBSD/
  }, {
    s: 'Sun OS',
    r: /SunOS/
  }, {
    s: 'Linux',
    r: /(Linux|X11)/
  }, {
    s: 'iOS',
    r: /(iPhone|iPad|iPod)/
  }, {
    s: 'Mac OS X',
    r: /Mac OS X/
  }, {
    s: 'Mac OS',
    r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/
  }, {
    s: 'QNX',
    r: /QNX/
  }, {
    s: 'UNIX',
    r: /UNIX/
  }, {
    s: 'BeOS',
    r: /BeOS/
  }, {
    s: 'OS/2',
    r: /OS\/2/
  }, {
    s: 'Search Bot',
    r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/
  }]
  for (var i = 0, cs; cs = clientStrings[i]; i++) {
    if (cs.r.test(nAgt)) {
      os = cs.s
      break
    }
  }

  var osVersion = unknown

  if (/Windows/.test(os)) {
    if (/Windows (.*)/.test(os)) {
      osVersion = /Windows (.*)/.exec(os)[1]
    }
    os = 'Windows'
  }

  switch (os) {
    case 'Mac OS X':
      if (/Mac OS X (10[\.\_\d]+)/.test(nAgt)) {
        osVersion = /Mac OS X (10[\.\_\d]+)/.exec(nAgt)[1]
      }
      break
    case 'Android':
      if (/Android ([\.\_\d]+)/.test(nAgt)) {
        osVersion = /Android ([\.\_\d]+)/.exec(nAgt)[1]
      }
      break
    case 'iOS':
      if (/OS (\d+)_(\d+)_?(\d+)?/.test(nAgt)) {
        osVersion = /OS (\d+)_(\d+)_?(\d+)?/.exec(nVer)
        osVersion = osVersion[1] + '.' + osVersion[2] + '.' + (osVersion[3] | 0)
      }
      break
  }

  return {
    osName: os + osVersion,
    type: 'desktop'
  }
}

/**
 * 获取操作系统
 * */
export function getOS() {
  if (isMobileType.any()) {
    return isMobileType.getOsName()
  } else {
    return detectDesktopOS()
  }
}
/**
 * 获取浏览器
 * */
export function getBrowser() {
  var sys = {}
  var ua = navigator.userAgent.toLowerCase()
  var s;
  (s = ua.match(/edge\/([\d.]+)/))
    ? (sys.edge = s[1])
    : (s = ua.match(/rv:([\d.]+)\) like gecko/))
      ? (sys.ie = s[1])
      : (s = ua.match(/msie ([\d.]+)/))
        ? (sys.ie = s[1])
        : (s = ua.match(/firefox\/([\d.]+)/))
          ? (sys.firefox = s[1])
          : (s = ua.match(/chrome\/([\d.]+)/))
            ? (sys.chrome = s[1])
            : (s = ua.match(/opera.([\d.]+)/))
              ? (sys.opera = s[1])
              : (s = ua.match(/version\/([\d.]+).*safari/))
                ? (sys.safari = s[1])
                : 0

  if (sys.edge) return { broswer: 'Edge', version: sys.edge }
  if (sys.ie) return { broswer: 'IE', version: sys.ie }
  if (sys.firefox) return { broswer: 'Firefox', version: sys.firefox }
  if (sys.chrome) return { broswer: 'Chrome', version: sys.chrome }
  if (sys.opera) return { broswer: 'Opera', version: sys.opera }
  if (sys.safari) return { broswer: 'Safari', version: sys.safari }

  return { broswer: '', version: '0' }
}
/**
 * @author AgentStore
 * @description removeEventListener
 * @type {function(...[*]=)}
 */
export const off = (function() {
  return function(element, event, handler, useCapture = false) {
    if (element && event) {
      element.removeEventListener(event, handler, useCapture)
    }
  }
})()

/**
 * 从 window.location.href 中获取指定key的value
 * @param {*} key 要获取的 key
 * @returns window.location.href 中指定key对应的value
 * @example
 * const value = getUrlParam(key);
 */
export function getUrlParam(key) {
  const url = window.location.href.replace(/^[^?]*\?/, '')
  const regexp = new RegExp(`(^|&)${key}=([^&#]*)(&|$|)`, 'i')
  const paramMatch = url.match(regexp)

  return paramMatch ? paramMatch[2] : null
}

export function httpBuildQuery(params){
  return Object.keys(params).map((key) => {
    if (Array.isArray(params[key])) {
      return params[key].map(value => `${encodeURIComponent(key)}[]=${encodeURIComponent(value)}`).join('&')
    }
    return `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
  }).join('&')
}

/**
* 计算字符串长度
* */
export function strLen(str) {
  if (typeof str === 'string') {
    return str.replace(/[^\x00-\xff]/g, '01').length
  }else{
    return 0
  }
}
/**
* 字符串截取支持中文截取
* */
export function  cutByte(str, len, endstr) {
  var len = +len,
    endstr = typeof (endstr) === 'undefined' ? '' : endstr.toString(),
    endstrBl = this.strLen(endstr)
  function n2(a) { var n = a / 2 | 0; return (n > 0 ? n : 1) }// 用于二分法查找
  if (!(str + '').length || !len || len <= 0) {
    return ''
  }
  if (len < endstrBl) {
    endstr = ''
    endstrBl = 0
  }
  var lenS = len - endstrBl,
    _lenS = 0,
    _strl = 0
  while (_strl <= lenS) {
    var _lenS1 = n2(lenS - _strl),
      addn = this.strLen(str.substr(_lenS, _lenS1))
    if (addn == 0) { return str }
    _strl += addn
    _lenS += _lenS1
  }
  if (str.length - _lenS > endstrBl || this.strLen(str.substring(_lenS - 1)) > endstrBl) {
    return str.substr(0, _lenS - 1) + endstr
  } else {
    return str
  }
}

/**
 * 通用名称构造方案
 * 仅支持大小写字母，数字、下划线、破折号、空格、小数点、中文，将不符合的字符默认替换为-
 * 中间多个空格替换为 一个空格
 * */
export function commonNameBuild(str) {
  const nameRegex = /[^\u4e00-\u9fa5a-zA-Z0-9_\-\s.]+/g;
  const duplicateDashRegex = /-+/g;
  const duplicateSpaceRegex = /\s+/g;
  if (typeof str === 'string') {
    let sanitizedName = str.replace(nameRegex, '-');
    sanitizedName = sanitizedName.replace(duplicateDashRegex, '-');
    sanitizedName = sanitizedName.replace(duplicateSpaceRegex, ' ');
    return sanitizedName
  }else{
    return ''
  }
}

export function download(url) {
  let iframe = document.createElement('iframe')
  iframe.src = url
  iframe.style.display = 'none'
  document.body.append(iframe)
}


export function processMarkdown(markdown) {
  if (!markdown) return '';

  // 1. 修复未闭合的代码块
  function fixIncompleteCodeBlocks(text) {
    // 跟踪代码块状态
    let lines = text.split('\n');
    let inCodeBlock = false;
    let codeBlockStart = -1;
    let result = [];

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];

      // 检测代码块标记
      if (line.trim().startsWith('```')) {
        if (!inCodeBlock) {
          // 开始新的代码块
          inCodeBlock = true;
          codeBlockStart = i;
        } else {
          // 结束当前代码块
          inCodeBlock = false;
        }
      }
      result.push(line);
    }

    // 如果文档结束时仍在代码块内,添加结束标记
    if (inCodeBlock) {
      result.push('```');
      console.log('已自动修复未闭合的代码块');
    }

    return result.join('\n');
  }

  // 2. 验证修复结果
  function validateMarkdown(text) {
    try {
      const startCount = (text.match(/```\w*/g) || []).length;
      const endCount = (text.match(/```\s*$/gm) || []).length;

      if (startCount !== endCount) {
        console.log('代码块数量不匹配，开始标记:', startCount, '结束标记:', endCount);
        return false;
      }
      return true;
    } catch (e) {
      console.log('验证过程出错:', e);
      return false;
    }
  }

  try {
    // 3. 处理流程
    let processedMarkdown = fixIncompleteCodeBlocks(markdown);

    // 验证处理结果
    if (!validateMarkdown(processedMarkdown)) {
      console.log('Markdown格式可能存在问题，但将继续处理');
    }

    return processedMarkdown;
  } catch (e) {
    console.error('Markdown处理错误:', e);
    return markdown; // 发生错误时返回原始文本
  }
}

export function stripMarkdown(markdownText) {
  // 1. 移除标题标记 (#)
  let plainText = markdownText.replace(/^#+\s*(.*)$/gm, '$1');
  // 2. 移除粗体/斜体标记 (**, *, __, _)
  plainText = plainText.replace(/(\*\*|__)(.*?)\1/g, '$2'); // 粗体
  plainText = plainText.replace(/(\*|_)(.*?)\1/g, '$2');   // 斜体
  // 3. 移除链接 ([text](url))
  plainText = plainText.replace(/\[(.*?)\]\(.*?\)/g, '$1');
  // 4. 移除图片 (![alt](url))
  plainText = plainText.replace(/!\[(.*?)\]\(.*?\)/g, '$1');
  // 5. 移除代码块 (```) 和行内代码 (`)
  plainText = plainText.replace(/```[\s\S]*?```/g, ''); // 多行代码块
  plainText = plainText.replace(/`(.*?)`/g, '$1');       // 行内代码
  // 6. 移除列表标记 (*, -, +)
  plainText = plainText.replace(/^[*-+]\s/gm, '');
  // 7. 移除引用块 (>)
  plainText = plainText.replace(/^>\s*/gm, '');
  // 8. 移除水平线 (---, ***, ___)
  plainText = plainText.replace(/^[*-]{3,}\s*$/gm, '');
  // 9. 移除多余的空格和换行符（根据标题需求调整）
  plainText = plainText.replace(/\s+/g, ' ').trim(); // 将多个空格替换为单个空格，并去除首尾空格



  return plainText;
}
